import { useEffect, useRef, useState } from "react";

const HOLD_MS = 5000;
// Primary phrases linger longer than examples so they register as the guiding hint.
const PRIMARY_HOLD_MS = 10000;

// A primary item is guaranteed after at most this many non-primary ones in a row.
const MAX_NON_PRIMARY_RUN = 3;

// Rotates through `items` (each `{ text, primary }`), holding each for HOLD_MS
// before moving on. Selection is random and non-repeating, but keeps primary items
// surfacing at least once every MAX_NON_PRIMARY_RUN + 1 changes. Returns the current
// item's text; the caller animates the transition between values. `enabled` gates
// the loop so it only runs while the placeholder is on screen.
export default function useRotatingPlaceholder(items, enabled = true) {
  const [text, setText] = useState("");
  const currentIndexRef = useRef(-1);
  const nonPrimaryRunRef = useRef(0);

  useEffect(() => {
    if (!enabled || !items || items.length === 0) {
      setText("");
      currentIndexRef.current = -1;
      nonPrimaryRunRef.current = 0;
      return undefined;
    }

    let timeoutId;

    const primaryIndexes = items.flatMap((item, i) => (item.primary ? [i] : []));
    const nonPrimaryIndexes = items.flatMap((item, i) =>
      item.primary ? [] : [i],
    );

    // Random pick from a pool, never repeating the item we just showed.
    const pickFrom = (pool) => {
      if (pool.length <= 1) return pool[0] ?? currentIndexRef.current;
      let next;
      do {
        next = pool[Math.floor(Math.random() * pool.length)];
      } while (next === currentIndexRef.current);
      return next;
    };

    const pickNextIndex = () => {
      // Never open on a primary — lead with an example.
      const isFirst = currentIndexRef.current === -1;
      if (isFirst && nonPrimaryIndexes.length > 0) return pickFrom(nonPrimaryIndexes);

      const forcePrimary =
        primaryIndexes.length > 0 &&
        nonPrimaryRunRef.current >= MAX_NON_PRIMARY_RUN;
      const pool = forcePrimary ? primaryIndexes : items.map((_, i) => i);
      return pickFrom(pool);
    };

    const showNext = () => {
      const nextIndex = pickNextIndex();
      const nextItem = items[nextIndex];
      currentIndexRef.current = nextIndex;
      nonPrimaryRunRef.current = nextItem.primary
        ? 0
        : nonPrimaryRunRef.current + 1;
      setText(nextItem.text);
      timeoutId = setTimeout(showNext, nextItem.primary ? PRIMARY_HOLD_MS : HOLD_MS);
    };

    showNext();
    return () => clearTimeout(timeoutId);
  }, [enabled, items]);

  return text;
}
